import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { getServices } from '../lib/api'
import GlassCard from '../components/ui/GlassCard'
import ScrollReveal from '../components/ui/ScrollReveal'
import CTASection from '../components/sections/CTASection'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import Parallax from '../components/ui/Parallax'
import type { Service } from '../types'

export default function Services() {
  const [data, setData] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getServices().then((res) => {
      setData(res)
      setLoading(false)
    })
  }, [])

  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gray-950" />
        <Parallax speed={0.1} className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px]">
          <div className="w-full h-full rounded-full bg-premium-600/10 blur-[150px]" />
        </Parallax>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-blue bg-electric-blue/10 rounded-full border border-electric-blue/20"
          >
            What We Do
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white"
          >
            Our{' '}
            <span className="bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent">
              Services
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto"
          >
            End-to-end digital solutions crafted to elevate your brand, engage
            your audience, and drive measurable growth.
          </motion.p>
        </div>
      </section>

      {/* Services List */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <LoadingSpinner text="Loading services..." />
          ) : (
            <div className="space-y-8">
              {data.map((service, i) => (
                <ScrollReveal key={service.id} delay={i * 0.08}>
                  <GlassCard className="p-8 md:p-12">
                    <div className="grid md:grid-cols-5 gap-8 items-start">
                      {/* Icon */}
                      <div className="md:col-span-1">
                        <div
                          className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-3xl`}
                        >
                          {service.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="md:col-span-2">
                        <h2 className="text-2xl font-bold text-white mb-3">
                          {service.title}
                        </h2>
                        <p className="text-gray-400 leading-relaxed">
                          {service.description}
                        </p>
                      </div>

                      {/* Features */}
                      <div className="md:col-span-2">
                        <ul className="grid sm:grid-cols-2 gap-3">
                          {service.features.map((feature) => (
                            <li
                              key={feature}
                              className="flex items-center gap-3 text-sm text-gray-400"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-electric-blue flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </GlassCard>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </main>
  )
}
